'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import Script from 'next/script'
import Button from '@/components/ui/Button'

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

type TurnstileApi = {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string
      callback: (token: string) => void
      'error-callback'?: () => void
      'expired-callback'?: () => void
      appearance?: 'always' | 'execute' | 'interaction-only'
      theme?: 'light' | 'dark' | 'auto'
    }
  ) => string
  reset: (id?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

type CreateResponse =
  | { ok: true; id: string; status: string; cached: boolean }
  | { ok: false; error: string; message?: string; retryAfterSeconds?: number }

/**
 * The URL form. Turnstile renders invisibly for most people and only shows a
 * challenge when Cloudflare is unsure; the token travels with the create
 * request and is verified server-side before anything is spent.
 */
export default function AuditForm() {
  const router = useRouter()
  const [url, setUrl] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [widgetReady, setWidgetReady] = useState(!SITE_KEY)
  const widgetRef = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)

  const renderWidget = useCallback(() => {
    if (!SITE_KEY || !widgetRef.current || !window.turnstile || widgetId.current) return
    widgetId.current = window.turnstile.render(widgetRef.current, {
      sitekey: SITE_KEY,
      appearance: 'interaction-only',
      theme: 'auto',
      callback: (t) => {
        setToken(t)
        setWidgetReady(true)
      },
      'expired-callback': () => setToken(null),
      'error-callback': () => {
        setToken(null)
        setWidgetReady(true)
      },
    })
  }, [])

  useEffect(() => {
    if (window.turnstile) renderWidget()
  }, [renderWidget])

  const resetWidget = () => {
    setToken(null)
    if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (pending) return
    setError(null)
    if (!url.trim()) {
      setError('Enter the page you want audited.')
      return
    }
    if (SITE_KEY && !token) {
      setError('The anti-bot check is still running. Give it a second and try again.')
      return
    }
    setPending(true)
    try {
      const res = await fetch('/api/tools/seo-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, turnstileToken: token ?? undefined }),
      })
      const data = (await res.json()) as CreateResponse
      if (!data.ok) {
        setError(data.message ?? 'We could not start the audit. Try again.')
        if (data.error === 'captcha_failed') resetWidget()
        setPending(false)
        return
      }
      router.push(`/tools/technical-seo-audit/report/${data.id}`)
    } catch {
      setError('We could not reach the audit service. Check your connection and try again.')
      setPending(false)
    }
  }

  return (
    <form onSubmit={submit} className="card mt-10 p-6 sm:p-8" noValidate>
      {SITE_KEY && (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
          strategy="afterInteractive"
          onLoad={renderWidget}
        />
      )}
      <label className="block" htmlFor="audit-url">
        <span className="text-sm font-medium text-fg">Page URL</span>
        <input
          id="audit-url"
          type="url"
          inputMode="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/"
          autoComplete="url"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          aria-describedby="audit-url-hint"
          className={`mt-2 w-full rounded-md border bg-bg px-3 py-2.5 text-fg outline-none transition-colors focus:border-accent/60 ${
            error ? 'border-danger/50' : 'border-line'
          }`}
        />
        <span id="audit-url-hint" className="mt-1 block text-xs text-muted">
          Any public page. The audit takes about 90 seconds.
        </span>
      </label>
      <div ref={widgetRef} className="mt-4" />
      {error && (
        <p role="alert" className="mt-4 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Button type="submit" size="lg" disabled={pending || !widgetReady}>
          {pending ? 'Starting the audit' : 'Run the audit'}
        </Button>
        <span className="text-sm text-muted">Free. No email required.</span>
      </div>
      <p className="mt-6 text-sm leading-relaxed text-muted">
        What happens: we fetch the page and follow its redirects, read robots.txt and the sitemap,
        pull Google&apos;s real-user Core Web Vitals, run Lighthouse on mobile and desktop, then
        write a prioritised plan from what we found.
      </p>
    </form>
  )
}
